import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings → Easeedesk → SKUs si Commands list.
 *
 * Perechile vechi: `skus_list`, `skus_data`, `edit_sku`, `send_sku`,
 * `commands_list`, `commands_list_data`, `edit_command`,
 * `send_commands_list`, `sku_modules`, `save_sku_module_status`. Si filele
 * din fisa unui SKU, mai jos.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface SkuRow {
  ID: number
  sku: string
  sku_name: string
  sku_type: number
  device_type: number
  support_license: number
  proactive_support: number
  proactive_alarms_group: number
  devices: number
  auto_activation: number
  auto_assign_to_customer: number
  visible: number
  run_with_adam: number
  group_name: string | null
  commands_count: number | null
}

export interface CommandRow {
  ID: number
  command: string
  command_title: string
  command_type: number
  description: string
  message: string
  report_description_header: string
  report_description: string
}

export interface AlarmGroup {
  ID: number
  group_name: string
  company_id: number
}

const BASE = '/settings/easeedesk/skus'

export function skusShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string; alarm_groups: AlarmGroup[] }>(BASE),
  })
}

export function skusTableQuery(search: string, start: number, length: number) {
  const query = `draw=1&start=${start}&length=${length}&search[value]=${encodeURIComponent(search)}`
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'table', search, start, length],
    queryFn: () =>
      apiGet<{ draw: number; recordsTotal: number; recordsFiltered: number; data: SkuRow[] }>(
        `${BASE}/table?${query}`,
      ),
  })
}

export function skuQuery(sku: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'one', sku],
    queryFn: () => apiGet<{ sku: SkuRow; alarm_groups: AlarmGroup[] }>(`${BASE}/${sku}`),
    enabled: sku > 0,
  })
}

export interface SkuForm {
  sku: string
  sku_name: string
  sku_type: number
  device_type: number
  /** `-1` nelimitat, `0` nelicentiat, `1` = numarul de zile din casuta de alaturi. */
  support_license: number
  support_license_days: number
  proactive_support: number
  proactive_alarms_group: number
  devices: number
  auto_activation: number
  auto_assign_to_customer: number
  visible: number
  run_with_adam: number
}

export function createSku(form: SkuForm) {
  return apiPost<{ ok?: boolean; ID?: number }>(BASE, { ...form })
}

export function updateSku(sku: number, form: SkuForm) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${sku}`, { ...form })
}

export function skuModulesQuery(sku: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'modules', sku],
    queryFn: () =>
      apiGet<{ sku_id: number; modules: Array<{ id: number; name: string }>; enabled: number[] }>(
        `${BASE}/${sku}/modules`,
      ),
    enabled: sku > 0,
  })
}

export function setSkuModule(sku: number, moduleId: number, enabled: boolean) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${sku}/modules`, { module_id: moduleId, enabled: enabled ? 1 : 0 })
}

export function commandsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'commands'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string }>(`${BASE}/commands`),
  })
}

export function commandsTableQuery(search: string, start: number, length: number) {
  const query = `draw=1&start=${start}&length=${length}&search[value]=${encodeURIComponent(search)}`
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'commands', 'table', search, start, length],
    queryFn: () =>
      apiGet<{ draw: number; recordsTotal: number; recordsFiltered: number; data: CommandRow[] }>(
        `${BASE}/commands/table?${query}`,
      ),
  })
}

export function commandQuery(command: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'commands', 'one', command],
    queryFn: () => apiGet<{ command: CommandRow }>(`${BASE}/commands/${command}`),
    enabled: command > 0,
  })
}

export interface CommandForm {
  command: string
  command_title: string
  command_type: number
  description: string
  message: string
  report_description_header: string
  report_description: string
}

export function createCommand(form: CommandForm) {
  return apiPost<{ ok?: boolean; ID?: number }>(`${BASE}/commands`, { ...form })
}

export function updateCommand(command: number, form: CommandForm) {
  return apiPost<{ ok?: boolean }>(`${BASE}/commands/${command}`, { ...form })
}

/* ---------------------------------------------------------------------------
 *  FILELE DIN FISA UNUI SKU: Commands, Ticket settings, Workflows
 *
 * Perechile vechi: `commands_sku`, `send_commands`, `tickets_settings_sku`,
 * `send_ticket_settings_sku`, `get_subcategories_cat_ticket`,
 * `get_operation_subcategories_cat_ticket`, `workflows_sku`,
 * `send_workflows_sku`.
 * ------------------------------------------------------------------------- */

export interface AvailableCommand {
  ID: number
  command: string
  command_title: string
  message: string
}

export interface ActiveCommand {
  ID: number
  command_id: number
  command_order: number
  command_title: string
  command_type: number
}

export interface CommandsTab {
  sku_id: number
  note: string
  standard: { available: AvailableCommand[]; active: ActiveCommand[] }
  operator: { available: AvailableCommand[]; active: ActiveCommand[] }
}

export function skuCommandsTabQuery(sku: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'commands_tab', sku],
    queryFn: () => apiGet<CommandsTab>(`${BASE}/${sku}/commands`),
    enabled: sku > 0,
  })
}

/**
 * Lista e UNA SINGURA, in ordinea de executie, cu comenzile standard inaintea
 * celor de operator - pe ordinea asta se sprijina propozitia de sus a filei.
 */
export function saveSkuCommands(sku: number, commandIds: number[]) {
  return apiPost<{ ok?: boolean; saved?: number }>(`${BASE}/${sku}/commands`, { commands: commandIds })
}

export interface TicketSettingsPanel {
  kind: number
  title: string
  label: string
  saved: {
    ticket_type: number
    category: number
    subcategory: number
    operation: number
    priority: number
    group: number
  }
  subcategories: { ID: number; subcategory_name: string }[]
  operations: { ID: number; operation_name: string }[]
}

export interface TicketSettingsTab {
  sku_id: number
  has_operator_commands: boolean
  ticket_types: { ID: number; type_name: string; color_identifier: string }[]
  categories: { ID: number; category_name: string }[]
  priorities: { ID: number; priority_name: string }[]
  groups: { ID: number; name: string }[]
  panels: TicketSettingsPanel[]
}

export function skuTicketSettingsQuery(sku: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'ticket_settings', sku],
    queryFn: () => apiGet<TicketSettingsTab>(`${BASE}/${sku}/ticket_settings`),
    enabled: sku > 0,
  })
}

export function skuSubcategories(category: number) {
  return apiGet<{ subcategories: { ID: number; subcategory_name: string }[] }>(
    `${BASE}/categories/${category}/subcategories`,
  )
}

export function skuOperations(subcategory: number) {
  return apiGet<{ operations: { ID: number; operation_name: string }[] }>(
    `${BASE}/subcategories/${subcategory}/operations`,
  )
}

export interface TicketSettingsForm {
  ticket_type: number
  category: number
  subcategory: number
  operation: number
  priority: number
  group: number
}

export function saveSkuTicketSettings(sku: number, kind: number, form: TicketSettingsForm) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${sku}/ticket_settings/${kind}`, { ...form })
}

export interface WorkflowsPanel {
  kind: number
  title: string
  label: string
  statuses: { ID: number; status_name: string }[]
  /** sku_status -> ticket_status */
  chosen: Record<string, number>
}

export interface WorkflowsTab {
  sku_id: number
  sku_statuses: { id: number; name: string; class: string }[]
  warnings: string[]
  panels: WorkflowsPanel[]
}

export function skuWorkflowsQuery(sku: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'skus', 'workflows', sku],
    queryFn: () => apiGet<WorkflowsTab>(`${BASE}/${sku}/workflows`),
    enabled: sku > 0,
  })
}

/**
 * `statuses[<sku_status>] = <ticket_status>`. Ecranul vechi trimitea cheile
 * GOALE (`1=..&2=..`), amestecate cu tokenul in acelasi corp - un camp in plus
 * in formular ar fi intrat drept stare de SKU.
 */
export function saveSkuWorkflows(sku: number, kind: number, map: Record<number, number>) {
  const fields: Record<string, PostValue> = {}
  for (const [skuStatus, ticketStatus] of Object.entries(map)) {
    fields[`statuses[${skuStatus}]`] = ticketStatus
  }
  return apiPost<{ ok?: boolean; saved?: number }>(`${BASE}/${sku}/workflows/${kind}`, fields)
}
